import { create } from "zustand";
import { baseApi } from "./api.js";
import { errorText, useBase } from "./state.js";
import { useBalances } from "../state/balances.js";
import { useSession } from "../state/session.js";
import { toast } from "../ui/Toast.js";
import type { CollectionResult } from "./loop-types.js";
import { onRequestReadReset, onRequestScopeReset, requestScope } from "../api/scope.js";

interface Flight { id: string; resource: string; amount: number; x: number; y: number }
const requests = new Map<string, string>();
let queue: Promise<unknown> | null = null;
const pending = new Set<string>();
export const useColony = create<{ collecting: boolean; pendingRows: string[]; flights: Flight[]; clearFlights(): void; collect(rowId: string | null, origin?: { x: number; y: number }): Promise<CollectionResult | null> }>((set) => ({
  collecting: false, pendingRows: [], flights: [], clearFlights: () => set({ flights: [] }),
  collect: async (rowId, origin) => {
    const accountId = useSession.getState().account?.id;
    if (!accountId) return null;
    const { generation } = requestScope();
    const key = `${accountId}:${rowId ?? "all"}`;
    if (pending.has(key)) return null;
    pending.add(key);
    const requestId = requests.get(key) ?? crypto.randomUUID(); requests.set(key, requestId);
    const updatePending = () => set({ collecting: pending.size > 0, pendingRows: [...pending].map(value => value.slice(value.indexOf(":") + 1)) });
    updatePending();
    const current = () => requestScope().generation === generation && useSession.getState().account?.id === accountId;
    const execute = async () => {
      if (!current()) return null;
      try {
      const result = await (rowId ? baseApi.collect(rowId, requestId) : baseApi.collectAll(requestId));
      if (requests.get(key) === requestId) requests.delete(key);
      if (!current() || result.base.enclave.accountId !== accountId) return result;
      useBase.setState({ base: result.base, error: null }); useBalances.getState().setBalances(result.balances, result.collectionRef);
      if (!result.reused) set(state => ({ flights: [...state.flights, ...result.collected.filter(row => row.amount > 0).map((row, index) => ({ id: `${requestId}:${index}`, resource: row.resource, amount: row.amount, x: origin?.x ?? innerWidth / 2, y: origin?.y ?? innerHeight / 2 }))].slice(-40) }));
      return result;
      } catch (error) { if (current()) toast.error(errorText(error)); return null; }
      finally { if (current()) { pending.delete(key); updatePending(); } }
    };
    const operation = queue ? queue.then(execute, execute) : execute();
    queue = operation;
    try { return await operation; }
    finally { if (queue === operation) queue = null; }
  },
}));

onRequestReadReset(() => { queue = null; pending.clear(); useColony.setState({ collecting: false, pendingRows: [], flights: [] }); });
onRequestScopeReset(() => requests.clear());
